import React, { useEffect, useState, useRef } from "react";
import "../styles/RecentChatUserList.css";
import RecentChatUserListItem from "./shared/RecentChatUserListItem";
import { Loader, Button } from "../shared/ui";
import NoResults from "./shared/NoResults";
import DeleteConfirmationModal from "./Modals/DeleteConfirmationModal";
import {
    DELETE_CONFIRMATION_MODAL,
    BUTTON_TEXT, Sizes,
} from "../constants";
import { SearchFilter as searchFilter, SearchFilterLength } from "../constants/SearchFilter";
import { useHideOnScroll } from "../hooks/useHideOnScroll";
import { useOrientationClose } from "../hooks/useOrientationClose";
import { useAppointmentScheduler } from "../hooks/useAppointmentScheduler";

const RecentChatUserList = React.memo(function ({
    users = [],
    activeUserId,
    onUserSelect,
    onChatDeleteClick,
    searchValue,
    loading,
    onLoadMore,
    hasMore,
    onBlockUserClick,
    onOffset,
}) {
    const [menuState, setMenuState] = useState({
        open: false,
        userId: null,
        position: { top: 0, left: 0 },
    });

    const [filteredUsers, setFilteredUsers] = useState([]);
    const [, setOffset] = useState(0);
    const sidebarRef = useRef(null);

    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    useEffect(() => {
      setFilteredUsers(users || []);
      if (!loading && Array.isArray(users) && users.length > 0) {
        setHasLoadedOnce(true);
      }
    }, [users, loading]);

    const handleLoadMore = () => {
        if (searchValue && searchValue.length > 0) {
            setOffset((prev) => {
                const newOffset = prev + searchFilter.LIMIT;
                if (onOffset) onOffset(newOffset);
                return newOffset;
            });
        } else if (onLoadMore) {
            onLoadMore();
        }
    };

    const [deleteModal, setDeleteModal] = useState({
        open: false,
        user: null,
    });

    const handleEllipsisClick = (e, userId) => {
        e.stopPropagation();

        const rect = e.currentTarget.getBoundingClientRect();
        const isMobile = window.innerWidth <= Sizes.BREAKPOINT_MOBILE;
        const left = isMobile
          ? rect.left - Sizes.CONTEXT_MENU_WIDTH
          : rect.left + window.scrollX;

        setMenuState({
            open: true,
            userId,
            position: {
                top: rect.top + window.scrollY,
                left,
            },
        });
    };

    const handleModalCancel = () => setDeleteModal({ open: false, user: null });
    const handleModalDelete = () => {
        if (deleteModal.user) {
            onChatDeleteClick(deleteModal.user);
        }
        setDeleteModal({ open: false, user: null });
    };

    const handleDelete = () => {
        const user = Array.isArray(users)
            ? users.find((u) => u.id === menuState.userId)
            : null;
        if (user) {
            setDeleteModal({ open: true, user });
        }
        handleCloseMenu();
    };

    const { scheduleAppointment } = useAppointmentScheduler();
    const handleScheduleAppointment = () => {
      scheduleAppointment();
      handleCloseMenu();
    }

    const handleCloseMenu = () =>
        setMenuState({
            open: false,
            userId: null,
            position: { top: 0, left: 0 },
        });

    useHideOnScroll(sidebarRef, menuState.open, handleCloseMenu);

    useOrientationClose(menuState.open, handleCloseMenu);

    const filtered = filteredUsers.filter(
      (user) => user?.lastMessageDate !== null || user?.id === activeUserId
    );
    const isEmpty =
      hasLoadedOnce &&
      !loading &&
      filtered.length === 0 &&
      (!searchValue || searchValue.length >= SearchFilterLength.RECENT);

    return (
      <>
        {loading ? (
          <Loader loading={loading} className="flex-center" />
        ) : isEmpty ? (
          <NoResults />
        ) : (
          <>
            <ul className="user-list" ref={sidebarRef}>
              {filteredUsers
                .filter(
                  (user) =>
                    user?.lastMessageDate !== null || user?.id === activeUserId
                )
                .map((user) => (
                  <RecentChatUserListItem
                    key={user?.id}
                    user={user}
                    activeUserId={activeUserId}
                    onUserSelect={onUserSelect}
                    onEllipsisClick={handleEllipsisClick}
                    menuState={menuState}
                    handleDelete={handleDelete}
                    handleScheduleAppointment={handleScheduleAppointment}
                    handleCloseMenu={handleCloseMenu}
                    onBlockUserClick={onBlockUserClick}
                  />
                ))}
            </ul>
            <div>
              {onLoadMore && hasMore && (
                <Button
                  variant="outline"
                  className="flex-center w-full"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? BUTTON_TEXT.LOADING : BUTTON_TEXT.LOAD_MORE}
                </Button>
              )}
            </div>
            <DeleteConfirmationModal
              isOpen={deleteModal.open}
              toggle={handleModalCancel}
              onDelete={handleModalDelete}
              deleteTitle={DELETE_CONFIRMATION_MODAL.TITLE}
              deleteMessage={DELETE_CONFIRMATION_MODAL.MESSAGE}
            />
          </>
        )}
      </>
    );
});

RecentChatUserList.displayName = "RecentChatUserList";
export default RecentChatUserList;